import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API_BASE = import.meta?.env?.VITE_API_URL || "https://bmtvendorvendorfinal.onrender.com";
const BASE = `${API_BASE}/api/nightclub-event-bookings/vendor`;

const getToken = () => {
  const v = localStorage.getItem("vendorToken");
  return v && v !== "null" && v !== "undefined" ? v : "";
};

async function api(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    if (res.status === 404 && /route not found/i.test(data.message || "")) {
      throw new Error("Bookings API server par mount nahi hai. server.js aur routes file check karo.");
    }
    throw new Error(
      data.message ||
        (res.status === 401 ? "Session expired. Please log in again." : "Something went wrong")
    );
  }
  return data;
}

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const eventDateOf = (b) => {
  const d = new Date(b?.eventId?.eventDate);
  return isNaN(d) ? null : d;
};

const isFutureEvent = (b) => {
  const d = eventDateOf(b);
  return !!d && d >= startOfToday();
};

const TABS = [
  {
    key: "upcoming",
    label: "Upcoming",
    hint: "Events yet to happen",
    test: (b) => ["pending", "confirmed"].includes(b.bookingStatus) && isFutureEvent(b),
    empty: "No upcoming bookings.",
  },
  {
    key: "pending",
    label: "Pending",
    hint: "Waiting for confirmation",
    test: (b) => b.bookingStatus === "pending",
    empty: "No pending bookings.",
  },
  {
    key: "confirmed",
    label: "Confirmed",
    hint: "Accepted bookings",
    test: (b) => b.bookingStatus === "confirmed",
    empty: "No confirmed bookings yet.",
  },
  {
    key: "completed",
    label: "Completed",
    hint: "Guests who attended",
    test: (b) => b.bookingStatus === "completed",
    empty: "No completed bookings yet.",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    hint: "Cancelled or refunded",
    test: (b) => ["cancelled", "refunded"].includes(b.bookingStatus),
    empty: "No cancelled bookings.",
  },
];

const money = (n, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch {
    return `${currency || "INR"} ${Number(n) || 0}`;
  }
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d)
    ? "—"
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d)
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const cap = (s = "") => String(s).charAt(0).toUpperCase() + String(s).slice(1);
const label = (s = "") => cap(String(s).replace(/_/g, " "));

const canCancel = (b) => ["pending", "confirmed"].includes(b?.bookingStatus);
const canDelete = (b) =>
  b?.bookingStatus === "cancelled" ||
  (b?.bookingStatus === "pending" && b?.paymentStatus !== "paid");

export default function NightclubBookings() {
  const rootRef = useRef(null);

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [tab, setTab] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [eventFilter, setEventFilter] = useState("");

  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: "start" });
  }, [detailId]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const d = await api(BASE);
      setBookings(Array.isArray(d.data) ? d.data : []);
    } catch (e) {
      setError(e.message);
      setBookings([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => {
      c[t.key] = bookings.filter(t.test).length;
    });
    return c;
  }, [bookings]);

  const stats = useMemo(() => {
    const live = bookings.filter((b) => !["cancelled", "refunded"].includes(b.bookingStatus));
    return {
      revenue: live
        .filter((b) => b.paymentStatus === "paid")
        .reduce((s, b) => s + (b.totalAmount || 0), 0),
      awaiting: live
        .filter((b) => b.paymentStatus !== "paid")
        .reduce((s, b) => s + (b.totalAmount || 0), 0),
      guests: bookings
        .filter((b) => b.bookingStatus === "confirmed" && isFutureEvent(b))
        .reduce((s, b) => s + (b.totalPersons || 0), 0),
      currency: bookings[0]?.currency || "INR",
    };
  }, [bookings]);

  const eventOptions = useMemo(() => {
    const map = new Map();
    bookings.forEach((b) => {
      const e = b.eventId;
      if (e?._id && !map.has(e._id)) map.set(e._id, e);
    });
    return [...map.values()].sort(
      (a, b) => new Date(b.eventDate || 0) - new Date(a.eventDate || 0)
    );
  }, [bookings]);

  const activeTab = TABS.find((t) => t.key === tab);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = bookings.filter((b) => {
      if (!activeTab.test(b)) return false;
      if (paymentFilter && b.paymentStatus !== paymentFilter) return false;
      if (eventFilter && b.eventId?._id !== eventFilter) return false;
      if (!q) return true;
      return [
        b.bookingId,
        b.customerName,
        b.customerEmail,
        b.customerPhone,
        b.ticketName,
        b.eventId?.title,
        b.nightClubId?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
    if (tab === "upcoming") {
      list.sort(
        (a, b) => (eventDateOf(a) || 0) - (eventDateOf(b) || 0)
      );
    }
    return list;
  }, [bookings, activeTab, tab, search, paymentFilter, eventFilter]);

  const detail = detailId ? bookings.find((b) => b._id === detailId) : null;

  const openDetail = (b) => {
    setCancelReason("");
    setDetailId(b._id);
  };

  const closeDetail = () => {
    if (busy) return;
    setDetailId(null);
    setCancelReason("");
  };

  const replaceBooking = (updated) => {
    setBookings((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));
  };

  const act = async (b, action) => {
    const confirmText = {
      confirm:
        b.paymentStatus === "paid"
          ? "Confirm this booking?"
          : "Payment is not received yet. Confirm this booking anyway?",
      "check-in": `Check in ${b.customerName}?`,
      complete: "Mark this booking as completed?",
      cancel:
        b.paymentStatus === "paid"
          ? "Payment is already received. Cancel this booking? You will need to process the refund."
          : "Cancel this booking?",
    };
    if (!window.confirm(confirmText[action])) return;

    setBusy(`${action}:${b._id}`);
    setError("");
    setNotice("");
    try {
      const d = await api(`${BASE}/${b._id}/${action}`, {
        method: "PATCH",
        body: JSON.stringify(action === "cancel" ? { reason: cancelReason } : {}),
      });
      if (d.data) replaceBooking(d.data);
      else await load(true);
      setNotice(d.message || "Booking updated.");
      if (action === "cancel") {
        setCancelReason("");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const remove = async (b) => {
    if (!window.confirm("Delete this booking permanently? This cannot be undone.")) return;
    setBusy(`delete:${b._id}`);
    setError("");
    setNotice("");
    try {
      const d = await api(`${BASE}/${b._id}`, { method: "DELETE" });
      setBookings((prev) => prev.filter((x) => x._id !== b._id));
      setDetailId(null);
      setNotice(d.message || "Booking deleted.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const isBusy = (action, id) => busy === `${action}:${id}`;

  const rowActions = (b, compact) => {
    const cls = `btn${compact ? " sm" : ""}`;
    return (
      <>
        {b.bookingStatus === "pending" && (
          <button
            className={`${cls} green`}
            disabled={!!busy}
            onClick={(e) => {
              e.stopPropagation();
              act(b, "confirm");
            }}
          >
            {isBusy("confirm", b._id) ? "Confirming…" : "Confirm"}
          </button>
        )}
        {b.bookingStatus === "confirmed" && b.entryStatus !== "checked_in" && (
          <button
            className={`${cls} primary`}
            disabled={!!busy}
            onClick={(e) => {
              e.stopPropagation();
              act(b, "check-in");
            }}
          >
            {isBusy("check-in", b._id) ? "Checking in…" : "Check in"}
          </button>
        )}
        {b.bookingStatus === "confirmed" && (
          <button
            className={cls}
            disabled={!!busy}
            onClick={(e) => {
              e.stopPropagation();
              act(b, "complete");
            }}
          >
            {isBusy("complete", b._id) ? "Saving…" : "Mark completed"}
          </button>
        )}
      </>
    );
  };

  return (
    <div className="neb" ref={rootRef}>
      <style>{CSS}</style>

      {detail ? (
        <DetailPage
          b={detail}
          busy={busy}
          isBusy={isBusy}
          cancelReason={cancelReason}
          setCancelReason={setCancelReason}
          onBack={closeDetail}
          onAct={act}
          onDelete={remove}
          error={error}
          notice={notice}
          clearError={() => setError("")}
        />
      ) : (
        <>
          <header className="neb-head">
            <div>
              <h1>Event bookings</h1>
              <p>Confirm new bookings, check guests in at the door and review past bookings.</p>
            </div>
            <button className="btn" onClick={() => load(true)} disabled={loading || refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </header>

          {error && (
            <div className="alert bad" role="alert">
              <span>{error}</span>
              <button onClick={() => setError("")} aria-label="Dismiss">×</button>
            </div>
          )}
          {notice && (
            <div className="alert good" role="status">
              <span>{notice}</span>
              <button onClick={() => setNotice("")} aria-label="Dismiss">×</button>
            </div>
          )}

          <section className="neb-stats">
            <Stat label="Revenue (paid)" value={money(stats.revenue, stats.currency)} />
            <Stat label="Awaiting payment" value={money(stats.awaiting, stats.currency)} />
            <Stat label="Guests expected" value={stats.guests} />
            <Stat label="Total bookings" value={bookings.length} />
          </section>

          <div className="neb-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                className={`neb-tab ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                <span className="neb-tab-text">
                  <strong>{t.label}</strong>
                  <small>{t.hint}</small>
                </span>
                <span className="neb-tab-count">{counts[t.key] || 0}</span>
              </button>
            ))}
          </div>

          <div className="neb-filters">
            <input
              type="search"
              placeholder="Search booking ID, guest, event or ticket"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}>
              <option value="">All events</option>
              {eventOptions.map((e) => (
                <option key={e._id} value={e._id}>
                  {e.title} {e.eventDate ? `– ${fmtDate(e.eventDate)}` : ""}
                </option>
              ))}
            </select>
            <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option value="">All payments</option>
              <option value="pending">Payment pending</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>

          <div className="neb-card">
            <div className="neb-card-head">
              <h2>{activeTab.label} bookings</h2>
              <span className="muted">
                {filtered.length} booking{filtered.length === 1 ? "" : "s"}
              </span>
            </div>

            {loading ? (
              <div className="neb-skel">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="skeleton" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="empty">
                <h3>
                  {search || paymentFilter || eventFilter ? "No matching bookings" : activeTab.empty}
                </h3>
                {(search || paymentFilter || eventFilter) && (
                  <p>Try clearing the search or filters.</p>
                )}
              </div>
            ) : (
              <div className="neb-table-wrap">
                <table className="neb-table">
                  <thead>
                    <tr>
                      <th>Booking</th>
                      <th>Guest</th>
                      <th>Event</th>
                      <th>Ticket</th>
                      <th>Guests</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((b) => (
                      <tr key={b._id} onClick={() => openDetail(b)}>
                        <td>
                          <strong>{b.bookingId || "—"}</strong>
                          <small>Booked {fmtDate(b.createdAt)}</small>
                        </td>
                        <td>
                          <strong>{b.customerName || "—"}</strong>
                          <small>{b.customerPhone || b.customerEmail || "—"}</small>
                        </td>
                        <td>
                          <strong>{b.eventId?.title || "Event"}</strong>
                          <small>
                            {fmtDate(b.eventId?.eventDate)}
                            {b.eventId?.startTime ? ` · ${b.eventId.startTime}` : ""}
                          </small>
                        </td>
                        <td>
                          <strong>{b.ticketName || "—"}</strong>
                          <small>
                            {b.quantity} × {money(b.pricePerTicket, b.currency)}
                          </small>
                        </td>
                        <td>{b.totalPersons}</td>
                        <td>
                          <strong>{money(b.totalAmount, b.currency)}</strong>
                          <small>
                            <span className={`pill pay-${b.paymentStatus}`}>{b.paymentStatus}</span>
                          </small>
                        </td>
                        <td>
                          <span className={`pill st-${b.bookingStatus}`}>{b.bookingStatus}</span>
                          {b.entryStatus === "checked_in" && (
                            <small>
                              <span className="pill st-confirmed">checked in</span>
                            </small>
                          )}
                        </td>
                        <td>
                          <div className="row-actions">
                            {rowActions(b, true)}
                            {canDelete(b) && b.bookingStatus === "cancelled" && (
                              <button
                                className="btn sm danger-ghost"
                                disabled={!!busy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remove(b);
                                }}
                              >
                                Delete
                              </button>
                            )}
                            <button
                              className="btn sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(b);
                              }}
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  function DetailPage(props) {
    return <BookingDetail {...props} rowActions={rowActions} />;
  }
}

function Stat({ label, value }) {
  return (
    <div className="stat">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function Info({ label: l, value }) {
  return (
    <div className="info">
      <span>{l}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}

function BookingDetail({
  b,
  busy,
  isBusy,
  cancelReason,
  setCancelReason,
  onBack,
  onAct,
  onDelete,
  error,
  notice,
  clearError,
  rowActions,
}) {
  const ev = b.eventId || {};
  const club = b.nightClubId || {};

  return (
    <div className="detail">
      <button type="button" className="back" onClick={onBack} disabled={!!busy}>
        ← Back to bookings
      </button>

      <header className="neb-head">
        <div>
          <h1>{b.bookingId || "Booking"}</h1>
          <div className="badges">
            <span className={`pill st-${b.bookingStatus}`}>{b.bookingStatus}</span>
            <span className={`pill pay-${b.paymentStatus}`}>payment {b.paymentStatus}</span>
            {b.entryStatus === "checked_in" && <span className="pill st-confirmed">checked in</span>}
          </div>
        </div>
        <div className="detail-total">
          <span>Total</span>
          <strong>{money(b.totalAmount, b.currency)}</strong>
        </div>
      </header>

      {error && (
        <div className="alert bad" role="alert">
          <span>{error}</span>
          <button onClick={clearError} aria-label="Dismiss">×</button>
        </div>
      )}
      {notice && (
        <div className="alert good" role="status">
          <span>{notice}</span>
        </div>
      )}

      <div className="detail-grid">
        <section className="panel">
          <h2>Guest</h2>
          <div className="info-grid">
            <Info label="Name" value={b.customerName} />
            <Info label="Phone" value={b.customerPhone} />
            <Info label="Email" value={b.customerEmail} />
            <Info label="Booked on" value={fmtDateTime(b.createdAt)} />
          </div>
        </section>

        <section className="panel">
          <h2>Event</h2>
          <div className="info-grid">
            <Info label="Event" value={ev.title} />
            <Info label="Type" value={ev.eventType ? label(ev.eventType) : ""} />
            <Info label="Date" value={fmtDate(ev.eventDate)} />
            <Info
              label="Time"
              value={[ev.startTime, ev.endTime].filter(Boolean).join(" – ")}
            />
            <Info label="Night club" value={club.name} />
            <Info label="City" value={club.city} />
          </div>
        </section>

        <section className="panel">
          <h2>Ticket</h2>
          <div className="info-grid">
            <Info label="Ticket" value={b.ticketName} />
            <Info label="Type" value={b.ticketType ? label(b.ticketType) : ""} />
            <Info label="Quantity" value={b.quantity} />
            <Info label="Guests in total" value={b.totalPersons} />
            <Info label="Price per ticket" value={money(b.pricePerTicket, b.currency)} />
            <Info label="Guests per ticket" value={b.personsPerTicket} />
          </div>
        </section>

        <section className="panel">
          <h2>Payment</h2>
          <div className="info-grid">
            <Info label="Total amount" value={money(b.totalAmount, b.currency)} />
            <Info label="Status" value={label(b.paymentStatus)} />
            <Info label="Method" value={label(b.paymentMethod)} />
            <Info label="Paid on" value={fmtDateTime(b.paidAt)} />
            <Info label="Payment ID" value={b.paymentId} />
            <Info
              label="Payment window ends"
              value={b.paymentStatus === "pending" ? fmtDateTime(b.paymentExpiresAt) : ""}
            />
          </div>
        </section>

        <section className="panel">
          <h2>Entry</h2>
          <div className="info-grid">
            <Info label="Entry status" value={label(b.entryStatus || "not_checked")} />
            <Info label="Checked in at" value={fmtDateTime(b.checkedInAt)} />
            <Info label="Confirmed on" value={fmtDateTime(b.confirmedAt)} />
          </div>
        </section>

        {b.bookingStatus === "cancelled" && (
          <section className="panel">
            <h2>Cancellation</h2>
            <div className="info-grid">
              <Info label="Cancelled on" value={fmtDateTime(b.cancelledAt)} />
              <Info label="Reason" value={b.cancellationReason || "No reason given"} />
            </div>
            {b.paymentStatus === "paid" && (
              <p className="note warn">
                Payment was received for this booking. Please make sure the refund is processed.
              </p>
            )}
          </section>
        )}

        {canCancel(b) && (
          <section className="panel">
            <h2>Cancel booking</h2>
            <label className="field">
              <span>Reason (optional)</span>
              <textarea
                rows="3"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why is this booking being cancelled?"
              />
            </label>
          </section>
        )}
      </div>

      <div className="actionbar">
        <button type="button" className="btn" onClick={onBack} disabled={!!busy}>
          Close
        </button>
        <div className="actions">
          {canDelete(b) && (
            <button
              className="btn danger-ghost"
              disabled={!!busy}
              onClick={() => onDelete(b)}
            >
              {isBusy("delete", b._id) ? "Deleting…" : "Delete booking"}
            </button>
          )}
          {canCancel(b) && (
            <button
              className="btn danger-ghost"
              disabled={!!busy}
              onClick={() => onAct(b, "cancel")}
            >
              {isBusy("cancel", b._id) ? "Cancelling…" : "Cancel booking"}
            </button>
          )}
          {rowActions(b, false)}
        </div>
      </div>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;700&family=Figtree:wght@400;500;600&display=swap');

.neb{
  --bg:#ffffff; --panel:#ffffff; --soft:#f5f4f9; --line:#e4e1ee;
  --text:#1c1830; --muted:#6b657f;
  --accent:#5b3fd9; --accent-dark:#4a31b8; --accent-soft:#ece8fb;
  --good:#1a8a5a; --good-bg:#e5f6ee;
  --warn:#a76a00; --warn-bg:#fff3d9;
  --bad:#c42b46;  --bad-bg:#fdeaee;
  --info:#1d4ed8; --info-bg:#e3ecfd;
  font-family:'Figtree',system-ui,sans-serif; color:var(--text); background:var(--bg);
  min-height:100vh; padding:32px clamp(16px,4vw,48px); box-sizing:border-box;
}
.neb *{box-sizing:border-box}
.neb h1,.neb h2,.neb h3{font-family:'Bricolage Grotesque',system-ui,sans-serif;margin:0}
.neb-head{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;margin-bottom:22px}
.neb-head h1{font-size:clamp(26px,4vw,38px);font-weight:700}
.neb-head p{margin:6px 0 0;color:var(--muted)}
.muted{color:var(--muted);font-size:14px}

.neb input,.neb select,.neb textarea{
  background:#fff;border:1px solid var(--line);color:var(--text);border-radius:8px;
  padding:10px 12px;font:inherit;width:100%;
}
.neb input::placeholder,.neb textarea::placeholder{color:#a9a4ba}
.neb input:focus,.neb select:focus,.neb textarea:focus,.neb button:focus-visible{outline:2px solid var(--accent);outline-offset:1px}

.btn{background:#fff;color:var(--text);border:1px solid var(--line);border-radius:8px;padding:10px 16px;font:inherit;font-weight:600;cursor:pointer;white-space:nowrap}
.btn:hover:not(:disabled){border-color:var(--muted)}
.btn:disabled{opacity:.5;cursor:not-allowed}
.btn.sm{padding:6px 12px;font-size:13px}
.btn.primary{background:var(--accent);border-color:var(--accent);color:#fff}
.btn.primary:hover:not(:disabled){background:var(--accent-dark);border-color:var(--accent-dark)}
.btn.green{background:var(--good);border-color:var(--good);color:#fff}
.btn.danger-ghost{color:var(--bad)}
.btn.danger-ghost:hover:not(:disabled){border-color:var(--bad)}

.alert{display:flex;align-items:center;gap:10px;padding:12px 16px;border-radius:10px;font-size:14px;margin-bottom:16px}
.alert span{flex:1}
.alert button{background:none;border:0;font-size:20px;line-height:1;cursor:pointer;color:inherit}
.alert.bad{background:var(--bad-bg);color:var(--bad)}
.alert.good{background:var(--good-bg);color:var(--good)}

.neb-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px;margin-bottom:18px}
.stat{background:var(--soft);border-radius:10px;padding:14px 16px;display:flex;flex-direction:column;gap:2px}
.stat strong{font-family:'Bricolage Grotesque',sans-serif;font-size:26px;font-weight:700}
.stat span{color:var(--muted);font-size:13px}

.neb-tabs{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;margin-bottom:16px}
@media(max-width:1100px){.neb-tabs{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media(max-width:640px){.neb-tabs{grid-template-columns:repeat(2,minmax(0,1fr))}}
.neb-tab{display:flex;align-items:center;justify-content:space-between;gap:10px;text-align:left;padding:12px 14px;background:#fff;border:1.5px solid var(--line);border-radius:12px;cursor:pointer;font:inherit;color:var(--text);transition:border-color .15s,box-shadow .15s}
.neb-tab:hover{box-shadow:0 4px 14px rgba(28,24,48,.08)}
.neb-tab.active{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-soft)}
.neb-tab-text{display:flex;flex-direction:column;min-width:0}
.neb-tab-text strong{font-size:15px}
.neb-tab-text small{font-size:12px;color:var(--muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.neb-tab-count{font-family:'Bricolage Grotesque',sans-serif;font-size:24px;font-weight:700}
.neb-tab.active .neb-tab-count{color:var(--accent)}

.neb-filters{display:grid;grid-template-columns:2fr 1.4fr 1fr;gap:10px;margin-bottom:16px}
@media(max-width:800px){.neb-filters{grid-template-columns:1fr}}

.neb-card{background:#fff;border:1px solid var(--line);border-radius:12px;overflow:hidden}
.neb-card-head{display:flex;justify-content:space-between;align-items:center;padding:16px 20px;border-bottom:1px solid var(--line)}
.neb-card-head h2{font-size:18px}
.neb-table-wrap{overflow-x:auto}
.neb-table{width:100%;min-width:980px;border-collapse:collapse}
.neb-table th{text-align:left;font-size:12px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:var(--muted);padding:11px 16px;background:var(--soft);white-space:nowrap}
.neb-table td{padding:13px 16px;border-top:1px solid var(--line);font-size:14px;vertical-align:middle}
.neb-table tbody tr{cursor:pointer;transition:background .12s}
.neb-table tbody tr:hover{background:#faf9fd}
.neb-table td strong{display:block;font-weight:600}
.neb-table td small{display:block;margin-top:3px;font-size:12px;color:var(--muted)}
.row-actions{display:flex;gap:8px;flex-wrap:wrap}

.pill{display:inline-block;padding:3px 10px;border-radius:99px;font-size:12px;font-weight:600;text-transform:capitalize;background:var(--soft);color:var(--muted);white-space:nowrap}
.pill.st-confirmed,.pill.pay-paid{background:var(--good-bg);color:var(--good)}
.pill.st-pending,.pill.pay-pending{background:var(--warn-bg);color:var(--warn)}
.pill.st-cancelled,.pill.pay-failed{background:var(--bad-bg);color:var(--bad)}
.pill.st-completed,.pill.st-refunded,.pill.pay-refunded{background:var(--info-bg);color:var(--info)}

.empty{text-align:center;padding:56px 20px;color:var(--muted)}
.empty h3{font-size:19px;color:var(--text)}
.empty p{margin:8px 0 0}
.neb-skel{display:grid;gap:10px;padding:20px}
.skeleton{height:54px;border-radius:10px;background:linear-gradient(90deg,var(--soft),#fff,var(--soft));background-size:200% 100%;animation:nebsh 1.4s infinite}
@keyframes nebsh{to{background-position:-200% 0}}
@media(prefers-reduced-motion:reduce){.skeleton{animation:none}}

.detail{max-width:1100px;margin:0 auto}
.back{background:none;border:0;padding:0;margin-bottom:14px;color:var(--accent);font:inherit;font-weight:600;cursor:pointer}
.back:hover{text-decoration:underline}
.badges{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px}
.detail-total{text-align:right;display:flex;flex-direction:column}
.detail-total span{color:var(--muted);font-size:13px}
.detail-total strong{font-family:'Bricolage Grotesque',sans-serif;font-size:32px}
.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
@media(max-width:800px){.detail-grid{grid-template-columns:1fr}}
.panel{background:#fff;border:1px solid var(--line);border-radius:12px;padding:20px}
.panel h2{font-size:17px;margin-bottom:14px}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
@media(max-width:480px){.info-grid{grid-template-columns:1fr}}
.info{background:var(--soft);border-radius:10px;padding:10px 14px;display:flex;flex-direction:column;gap:2px;min-width:0}
.info span{font-size:12px;color:var(--muted)}
.info strong{font-size:14px;word-break:break-word}
.note{margin:12px 0 0;font-size:14px;border-radius:8px;padding:10px 12px}
.note.warn{background:var(--warn-bg);color:var(--warn)}
.field{display:flex;flex-direction:column;gap:6px}
.field>span{font-size:13px;color:var(--muted)}

.actionbar{position:sticky;bottom:0;z-index:5;margin-top:20px;background:#fff;border-top:1px solid var(--line);padding:14px 0;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap}
.actions{display:flex;gap:10px;flex-wrap:wrap}
`;